import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Employee_Api_EndPoints } from "../global/end-points";
import * as httpService from "../services/httpService";
import Cookies from "universal-cookie";
import CryptoJS from "crypto-js";
import useActivityLogger from "../utils/useActivityLogger";
import PrivilegeService from "../services/PrivilegeService"; // ✅ import PrivilegeService

function LoginOnBehalf() {
  const [mobileNumber, setMobileNumber] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const { logActivity, logClick, logApiCall } = useActivityLogger({ moduleCode: "DPY" });

  // ✅ Page view → log once
  useEffect(() => {
    logActivity({
      activity: "PAGE_VIEW",
      description: "OBO-LoginOnBehalf Page View",
      component: "OBO.Page.LoginOnBehalf",
    });
  }, [logActivity]);

  // Format as (XXX)-XXX-XXXX
  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, "").slice(0, 10);
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return value;

    let formatted = "";
    if (match[1]) formatted = `(${match[1]}`;
    if (match[2]) formatted += `) ${match[2]}`;   // 👈 space instead of dash
    if (match[3]) formatted += `-${match[3]}`;
    return formatted;
  };


  const handleInputChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setMobileNumber(formatted);
    setError("");

    const digitsOnly = formatted.replace(/\D/g, "");
    if (digitsOnly.length === 10) {
      logActivity({
        activity: "INPUT_COMPLETE",
        description: "OBO-Entered Mobile Number",
        component: "OBO.Input.MobileNumber",
      });
    }
  };

  const prevphone = sessionStorage.getItem("phoneNumber");

  const handleCancel = logClick("OBO.Button.Cancel", "OBO-Clicked Cancel", {
    onClick: () => {
      navigate("/home");
    },
  });

  const encryptionKey = "InstaDoughAES128";
  const key = CryptoJS.enc.Utf8.parse(encryptionKey);
  const iv = CryptoJS.enc.Utf8.parse("\0".repeat(16));

  function decryptPassword(encryptedText) {
    const decrypted = CryptoJS.AES.decrypt(encryptedText, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  const handleContinue = logClick("OBO.Button.Continue", "OBO-Clicked Continue", {
    onClick: async () => {
      const digitsOnly = mobileNumber.replace(/\D/g, "");

      if (!digitsOnly) {
        setError("Please enter the mobile number.");
        logActivity({
          activity: "VALIDATION_ERROR",
          description: "OBO-Mobile Number Missing",
          component: "OBO.Input.MobileNumber",
        });
        return;
      }

      if (prevphone === digitsOnly) {
        setError("The mobile number matches your login. Please use a different one to proceed.");
        logActivity({
          activity: "VALIDATION_ERROR",
          description: "OBO-Mobile Number Invalid - Same as own",
          component: "OBO.Input.MobileNumber",
        });
        return;
      }

      if (digitsOnly.length !== 10) {
        setError("Please enter a valid 10-digit mobile number.");
        logActivity({
          activity: "VALIDATION_ERROR",
          description: "OBO-Invalid Mobile Number",
          component: "OBO.Input.MobileNumber",
        });
        return;
      }

      // ---- API: Get User Details ----
      logApiCall("OBO.API.GetUserDetails", "OBO-Get User Details (request)");
      let userDetailsResponse;
      try {
        userDetailsResponse = await httpService.CallWithHeaders(
          "get",
          Employee_Api_EndPoints.GET_USER_DETAILS,
          { phoneNumber: digitsOnly },
          { SecretKey: "DoUgHPaYSeCrEKeY" }
        );
        logActivity({
          activity: "API_CALL",
          description: "OBO-Get User Details (success)",
          component: "OBO.API.GetUserDetails",
        });
      } catch {
        logActivity({
          activity: "API_CALL",
          description: "OBO-Get User Details (error)",
          component: "OBO.API.GetUserDetails",
        });
        setError("Failed to fetch user details.");
        return;
      }

      const data = userDetailsResponse?.data?.[0];
      if (!data?.userId) {
        setError("Mobile number is not registered");
        logActivity({
          activity: "BUSINESS_RULE",
          description: "OBO-No user found for mobile",
          component: "OBO.API.GetUserDetails",
        });
        return;
      }

      const decryptedPassword1 = decryptPassword(data.password);
      const cookies = new Cookies();

      // ---- API: Authorization ----
      logApiCall("OBO.API.Authorization", "OBO-Authorization (request)");
      let authResponse;
      try {
        authResponse = await httpService.Get(Employee_Api_EndPoints.AUTHORIZATION, {
          UserName: data.userName,
          Password: decryptedPassword1,
          EmployeeId: data.employeeId,
          PhoneNumber: digitsOnly,
        });
        if (!(authResponse?.status === 200 && authResponse.data)) {
          throw new Error("Bad auth response");
        }
        // logActivity({
        //   activity: "API_CALL",
        //   description: "OBO-Authorization (success)",
        //   component: "OBO.API.Authorization",
        // });
      } catch {
        // logActivity({
        //   activity: "API_CALL",
        //   description: "OBO-Authorization (error)",
        //   component: "OBO.API.Authorization",
        // });
        setError("Authorization failed while logging in on behalf.");
        return;
      }

      // ✅ Do not overwrite original UserId/session
      // Persist OnBehalf info separately
      sessionStorage.setItem("OnBehalfUserId", data.userId);
      sessionStorage.setItem("OnBehalfUserName", data.userName);
      sessionStorage.setItem("OnBehalfSSN", data.socialSecurityNumber);
      sessionStorage.setItem("OnBehalfPhoneNumber", digitsOnly);

      // Cookies remain for authentication context
      cookies.set("AccessToken", authResponse.data?.accessToken, { path: "/" });
      cookies.set("UserName", authResponse.data?.userName, { path: "/" });
      cookies.set("UserId", authResponse.data?.userID, { path: "/" });

      // ✅ Store OnBehalf token separately
      sessionStorage.setItem("OnBehalfAccessToken", authResponse.data?.accessToken);

      // ---- API: Fetch Privileges for OBO user ---- ✅ added here
      logApiCall("OBO.API.GetPrivileges", "OBO-Get Privileges (request)");
      try {
        await PrivilegeService.fetchPrivileges(digitsOnly, "DoughPay");
        logActivity({
          activity: "API_CALL",
          description: "OBO-Get Privileges (success)",
          component: "OBO.API.GetPrivileges",
        });
      } catch {
        logActivity({
          activity: "API_CALL",
          description: "OBO-Get Privileges (error)",
          component: "OBO.API.GetPrivileges",
        });
      }

      setError("");
      logActivity({
        activity: "NAVIGATE",
        description: "OBO-Navigate to /home",
        component: "OBO.Router",
        route: "/home",
      });
      // ✅ Fix back button: replace history
      navigate("/home", { replace: true });
    },
  });

  return (
    <div className="lob-fullpage">
      <div className="lob-card">
        <h3 className="lob-title">Login On Behalf</h3>

        <input
          type="text"
          placeholder="Enter Mobile Number"
          value={mobileNumber}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleContinue(e); // ✅ just call the function directly
            }
          }}
          className="lob-input"
          maxLength={14}
        />

        {error && <p className="lob-error">{error}</p>}

        <div className="lob-button-group">
          <button className="lob-cancel-btn" onClick={handleCancel}>
            Cancel
          </button>
          <button className="lob-continue-btn" onClick={handleContinue}>
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export default LoginOnBehalf;
